import { reactive } from 'vue';
import { defineStore } from 'pinia';
import api from '@/services/api';
import {
  Role,
  DESCRIPCION_EJECUTIVO_QUE_COTIZA,
} from '@/enum/roles_enum';

export const useAuthStore = defineStore('authStore', () => {
  const authData = reactive({
    Usuario: '',
    Password: '',
    mantenerSesion: false,
  });

  const authHttp = async () => {
    // withCredentials SOLO cuando se pidió mantener la sesión: en despliegues cross-origin el
    // browser descarta el Set-Cookie de `sispro_refresh` sin esto, pero pedir credenciales exige
    // que el backend tenga el origen en CORS_ORIGIN. Sin el toggle el login funciona desde
    // cualquier origen (como antes) y no se toca la cookie.
    let result;
    try {
      result = await api.post(
        '/auth',
        authData,
        authData.mantenerSesion ? { withCredentials: true } : undefined,
      );
    } catch (error) {
      // Error de red/CORS (sin respuesta del server) con el toggle activo: el origen del frontend
      // probablemente no está en CORS_ORIGIN. Se reintenta el login SIN credenciales para que el
      // usuario pueda entrar igual (sin sesión persistente) en vez de quedar bloqueado.
      if (authData.mantenerSesion && !error?.response) {
        console.warn(
          'Login con "Mantener sesión iniciada" bloqueado por CORS/red; reintentando sin cookie. ' +
            'Agregar este origen a CORS_ORIGIN del backend para habilitar la sesión persistente.',
        );
        result = await api.post('/auth', { ...authData, mantenerSesion: false });
      } else {
        throw error;
      }
    }
    if (result && result.data) {
      const { token, ...usuario } = result.data;
      localStorage.setItem('token', JSON.stringify(token));
      localStorage.setItem('usuario', JSON.stringify(usuario));
    }
  };

  const closeSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    // Limpiar los datos del formulario
    authData.Usuario = '';
    authData.Password = '';
    authData.mantenerSesion = false;
  };

  // Logout unificado (mobile y desktop): borra la cookie de refresh en el server
  // (best-effort: si falla se ignora) y limpia la sesión local. El redirect a '/'
  // lo hace el llamador (el store no importa el router para evitar dependencia circular).
  const cerrarSesionMobile = async () => {
    try {
      // data `undefined` (no `null`): con el Content-Type JSON por defecto de `api`, axios
      // serializaría `null` como body "null" y body-parser (strict) lo rechaza en el server;
      // el catch best-effort lo taparía y la cookie nunca se borraría.
      await api.post('/auth/logout', undefined, { withCredentials: true });
    } catch {
      // best-effort
    }
    closeSession();
  };

  const hasToken = () => {
    const token = localStorage.getItem('token');
    return !!(token && token !== '');
  };

  const usuario = () => {
    return JSON.parse(localStorage.getItem('usuario'));
  };

  const findRole = (CodigoRol, isOnly = true) => {
    const userLocalStorage = JSON.parse(localStorage.getItem('usuario'));
    if (isOnly === false) {
      return !(userLocalStorage.roles.findIndex((r) => r.Codigo === CodigoRol) >= 0);
    } else {
      return userLocalStorage.roles.length === 1 && userLocalStorage.roles[0].Codigo === CodigoRol;
    }
  };

  const hasRole = (codigoRol) => {
    const user = JSON.parse(localStorage.getItem('usuario'));
    return !!user?.roles?.some((r) => r.Codigo === codigoRol);
  };

  const esEjecutivoQueCotiza = () => {
    const user = JSON.parse(localStorage.getItem('usuario'));
    const descripcion = DESCRIPCION_EJECUTIVO_QUE_COTIZA.toLowerCase();
    const tieneRol = !!user?.roles?.some(
      (r) => r.Descripcion?.trim().toLowerCase() === descripcion,
    );
    return tieneRol && !hasRole(Role.ADMINISTRADOR);
  };

  return {
    authHttp,
    authData,
    closeSession,
    cerrarSesionMobile,
    hasToken,
    usuario,
    findRole,
    hasRole,
    esEjecutivoQueCotiza,
  };
});
